import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { UpdateChangeRequest } from 'services/ChangeRequestsService';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ChangeClientRefParams, ChangeRequest } from 'common/models/ChangeRequest';

interface Props {
  open: boolean;
  changeRequest: ChangeRequest;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (message: string) => void;
  errorHandler?: (message: string) => void;
}

export default function EditChangeClientRefDialog({
  open,
  changeRequest,
  closeHandler,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [clientRef, setClientRef] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<moment.Moment | null>(null);

  const p = useMemo<ChangeClientRefParams | null>(() => {
    if (changeRequest.json) return JSON.parse(changeRequest.json) as ChangeClientRefParams;
    return null;
  }, [changeRequest.json]);

  useEffect(() => {
    if (open) {
      const m = moment(changeRequest.effectiveDate);
      setEffectiveDate(m);
      setClientRef(p ? p.clientReference : '');
    }
  }, [open, changeRequest.effectiveDate, p]);

  const submitCallback = useCallback(async () => {
    const efDate =
      effectiveDate && effectiveDate.isValid() ? effectiveDate.format('YYYY-MM-DD') : '';
    const data: ChangeClientRefParams = {
      clientReference: clientRef,
      effectiveDate: efDate,
    };

    loadingHandler && loadingHandler(true);
    const res = await UpdateChangeRequest(
      changeRequest.id,
      changeRequest.changeTypeID,
      data,
      errorHandler
    );
    if (res) {
      successHandler && successHandler('Change Request Updated');
      closeHandler();
    }
    loadingHandler && loadingHandler(false);
  }, [
    effectiveDate,
    clientRef,
    loadingHandler,
    changeRequest.id,
    changeRequest.changeTypeID,
    errorHandler,
    successHandler,
    closeHandler,
  ]);

  return (
    <>
      <Dialog open={open} fullWidth>
        <DialogTitle>Change Client Ref</DialogTitle>
        <DialogContent>
          <Stack mt="10px" spacing={2}>
            <DatePicker
              label="Effective Date"
              value={effectiveDate}
              onChange={(m) => setEffectiveDate(m)}
            />
            <RWTextFieldComponent
              label="Client Ref"
              value={clientRef}
              onChange={(e) => setClientRef(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeHandler}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!effectiveDate}
            onClick={submitCallback}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
